import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { t, trRoomName, trServer, useLang } from "../lib/i18n";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { BrandBackground } from "../components/BrandBackground";
import { EMBLEM_URL, LOGO_URL } from "../lib/brand";
import { toast } from "../lib/notify";
import { startGoogleLogin } from "../lib/googleLogin";
import { nameError } from "../lib/playerName";
import { createGuest, resumeGuest, useKnownGuest } from "../lib/guestDevice";
import { KnownGuestButton, NewGuestToggle } from "../components/KnownGuest";
import {
  Users, Crown, Lock, RefreshCw, ChevronRight,
  AlertCircle, Loader2, ShieldCheck, Smartphone,
} from "lucide-react";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// status: "loading" | "open" | "full" | "started" | "not_found" | "error"
export default function JoinRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, setUser } = useAuth();
  useLang();

  const [roomInfo, setRoomInfo] = useState(null);
  const [status, setStatus] = useState("loading");

  const [guestName, setGuestName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // The guest this device keeps (lib/guestDevice.js), as on the home page.
  const [known, setKnown, knownReady] = useKnownGuest();
  const [newProfile, setNewProfile] = useState(false);
  const showKnown = !!known && !newProfile;

  const nameRef = useRef(null);
  const didAutoJoin = useRef(false);

  // Fetch room info (public, no auth needed)
  const fetchInfo = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}/info`);
      setRoomInfo(data);
      if (data.started) setStatus("started");
      else if (data.is_full) setStatus("full");
      else setStatus("open");
    } catch (e) {
      const code = e?.response?.status;
      setStatus(code === 404 ? "not_found" : "error");
    }
  }, [roomId]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  // Focus name field once room is confirmed open
  useEffect(() => {
    if (status === "open" && !user && knownReady && !showKnown) {
      setTimeout(() => nameRef.current?.focus(), 180);
    }
  }, [status, user, knownReady, showKnown]);

  // Authenticated user: auto-join and redirect
  useEffect(() => {
    if (authLoading || didAutoJoin.current) return;
    if (!user || status === "loading") return;
    didAutoJoin.current = true;

    if (status === "not_found" || status === "error") return;

    if (status === "started") {
      navigate(`/game/${roomId}`, { replace: true });
      return;
    }

    const doJoin = async () => {
      try {
        await api.post(`/rooms/${roomId}/join`);
        navigate(`/room/${roomId}`, { replace: true });
      } catch (e) {
        // Room became full between info fetch and join — re-fetch to show correct screen
        didAutoJoin.current = false;
        fetchInfo();
      }
    };
    doJoin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, status, roomId]);

  // Google: the website goes to Google and back to /auth/callback; the apps use an in-app tab and come
  // back into the app once signed in (lib/googleLogin.js). Either way the player lands back on the invite.
  const handleGoogleLogin = () => {
    startGoogleLogin(`/join/${roomId}`).catch(() => toast.error(t("common.googleFailed")));
  };

  // A guest, as on the home page: the name, then the profile is made and the room joined at once (the draw
  // pile's side is in the Lobby's Table tab, on the right by default).
  const handleGuestJoin = async () => {
    const name = guestName.trim();
    if (!name) { setError(t("common.enterName")); return; }
    // The server's rule for a name, said here first (lib/playerName.js).
    const bad = nameError(guestName);
    if (bad) { setError(bad); return; }
    setSubmitting(true);
    setError("");
    // Create guest account (token is saved → used by axios interceptor immediately)
    await enterAs(() => createGuest(name));
  };

  // The guest's account (a new one, or the device's), then the room.
  const enterAs = async (signIn) => {
    try {
      const guest = await signIn();

      // Join the room
      try {
        await api.post(`/rooms/${roomId}/join`);
      } catch (joinErr) {
        const detail = joinErr?.response?.data?.detail;
        setError(trServer(detail) || t("join.cantJoin"));
        setSubmitting(false);
        return;
      }

      // Set user in context and navigate
      setUser(guest);
      navigate(`/room/${roomId}`, { replace: true });
    } catch (err) {
      // The device's guest is gone: the name field takes its place.
      if (err.gone) { setKnown(null); setError(t("landing.guestGone")); }
      else setError(trServer(err?.response?.data?.detail) || t("common.networkRetry"));
      setSubmitting(false);
    }
  };

  const handleResumeJoin = async () => {
    setSubmitting(true);
    setError("");
    await enterAs(resumeGuest);
  };

  // ── Layout helpers ──────────────────────────────────────────────────

  const RoomCard = () => {
    if (!roomInfo) return null;
    const slots = Array.from({ length: roomInfo.max_players });
    const filledCount = roomInfo.members_count;
    return (
      <div className="rami-glass rounded-2xl p-5 mb-6 border border-amber-400/15">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-400/60 mb-1">
              {t("join.gameOf")}
            </div>
            <div className="text-xl font-display text-white">{trRoomName(roomInfo.name)}</div>
            <div className="flex items-center gap-1.5 mt-1 text-slate-400 text-sm">
              <Crown className="h-3.5 w-3.5 text-amber-400/70" />
              <span>{roomInfo.host_name}</span>
            </div>
          </div>
          <StatusBadge />
        </div>

        {/* Player slot dots */}
        <div className="flex items-center gap-2 mt-3">
          <Users className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <div className="flex gap-1.5">
            {slots.map((_, i) => (
              <div
                key={i}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                  i < filledCount
                    ? "bg-emerald-400"
                    : "bg-slate-700 border border-slate-600"
                }`}
              />
            ))}
          </div>
          <span className="text-slate-400 text-xs ms-1">
            {t("common.playersCount", { a: filledCount, b: roomInfo.max_players })}
          </span>
        </div>
      </div>
    );
  };

  const StatusBadge = () => {
    if (status === "started") {
      return (
        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-rose-500/15 text-rose-300 border border-rose-500/25 px-2.5 py-1 rounded-full">
          <Lock className="h-2.5 w-2.5" /> {t("join.status.started")}
        </span>
      );
    }
    if (status === "full") {
      return (
        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/25 px-2.5 py-1 rounded-full">
          <Users className="h-2.5 w-2.5" /> {t("join.status.full")}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 px-2.5 py-1 rounded-full">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        {t("join.status.open")}
      </span>
    );
  };

  // ── Screens ─────────────────────────────────────────────────────────

  // Auth loading or room info still loading
  if (authLoading || status === "loading") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  // Room not found / expired
  if (status === "not_found" || status === "error") {
    return (
      <PageWrapper>
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-8 w-8 text-rose-400" />
          </div>
          <h2 className="font-display text-2xl text-white mb-2">{t("join.invalidLink")}</h2>
          <p className="text-slate-400 text-sm mb-6">
            {t("join.invalidLinkDesc")}
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-full px-6"
          >
            {t("common.home")}
          </Button>
        </div>
      </PageWrapper>
    );
  }

  // Authenticated user — auto-join is in progress (useEffect)
  if (user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-amber-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">{t("join.connectingGame")}</p>
        </div>
      </div>
    );
  }

  // Room full — no slot for new guest
  if (status === "full") {
    return (
      <PageWrapper>
        <RoomCard />
        <div className="text-center bg-amber-500/[0.06] border border-amber-500/20 rounded-xl p-5 mb-5">
          <Users className="h-6 w-6 text-amber-400 mx-auto mb-2" />
          <div className="text-amber-300 font-semibold mb-1">{t("join.fullTitle")}</div>
          <p className="text-slate-400 text-sm">
            {t("join.fullDesc")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchInfo}
            variant="outline"
            className="flex-1 border-white/10 text-slate-300 rounded-full"
          >
            <RefreshCw className="h-3.5 w-3.5 me-1.5" /> {t("join.refresh")}
          </Button>
          <Button
            onClick={() => navigate("/")}
            className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-full"
          >
            {t("common.home")}
          </Button>
        </div>
      </PageWrapper>
    );
  }

  // Room started — cannot join mid-game (not already a member)
  if (status === "started") {
    return (
      <PageWrapper>
        <RoomCard />
        <div className="text-center bg-rose-500/[0.06] border border-rose-500/20 rounded-xl p-5 mb-5">
          <Lock className="h-6 w-6 text-rose-400 mx-auto mb-2" />
          <div className="text-rose-300 font-semibold mb-1">{t("join.startedTitle")}</div>
          <p className="text-slate-400 text-sm">
            {t("join.startedDesc")}
          </p>
        </div>
        <Button
          onClick={() => navigate("/")}
          className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-full"
        >
          {t("common.home")}
        </Button>
      </PageWrapper>
    );
  }

  // ── Main join form (status === "open", user not logged in) ──────────
  return (
    <PageWrapper>
      <AnimatePresence mode="wait">
        {(
          <motion.div
            key="name"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14, scale: 0.97 }}
            transition={{ duration: 0.28 }}
          >
            {/* Google first (owner's request): the account keeps the player's data. On the website and in the
                apps (owner's request, October 2026). */}
            <button
              onClick={handleGoogleLogin}
              data-testid="google-btn"
              className="group w-full flex items-center gap-4 bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 hover:border-white/30 rounded-xl p-4 text-start transition-all duration-200 cursor-pointer"
            >
              <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
                <GoogleIcon />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm">{t("common.google")}</div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-emerald-300/90">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                  <span>{t("landing.googleKeep")}</span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-slate-200 transition-colors shrink-0 rtl:rotate-180" />
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-3.5">
              <div className="flex-1 h-px bg-white/[0.07]" />
              <span className="text-slate-600 text-[11px] uppercase tracking-widest font-medium">{t("common.or")}</span>
              <div className="flex-1 h-px bg-white/[0.07]" />
            </div>

            {/* A guest: the device's one in one tap, or the name field, « Rejoindre » inside it; it is kept
                on this device. */}
            {knownReady && showKnown && (
              <div className="mb-3 w-full rounded-2xl p-px bg-gradient-to-br from-amber-400/60 via-amber-500/40 to-amber-600/20 shadow-lg shadow-amber-500/10">
                <div className="rounded-2xl bg-slate-900/90">
                  <KnownGuestButton guest={known} onClick={handleResumeJoin} busy={submitting} />
                </div>
              </div>
            )}
            {knownReady && !showKnown && (
            <div className="mb-3">
              <div className="relative">
                <input
                  ref={nameRef}
                  data-testid="guest-name-input"
                  type="text"
                  placeholder={t("common.namePlaceholder")}
                  maxLength={20}
                  value={guestName}
                  onChange={(e) => { setGuestName(e.target.value); setError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !submitting) handleGuestJoin(); }}
                  className="w-full bg-slate-800/50 border border-white/10 focus:border-amber-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-600 text-base transition-colors outline-none pe-[120px]"
                  autoComplete="off"
                />
                <button
                  type="button"
                  data-testid="guest-continue-btn"
                  onClick={handleGuestJoin}
                  disabled={!guestName.trim() || submitting}
                  className="absolute end-1.5 top-1/2 -translate-y-1/2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm rounded-lg px-3 py-1.5 transition-all whitespace-nowrap flex items-center gap-1.5"
                >
                  {submitting ? <span className="h-3.5 w-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : t("common.joinArrow")}
                </button>
              </div>
              <p className="mt-1.5 flex items-center gap-1 px-1 text-xs text-amber-300/70">
                <Smartphone className="h-3.5 w-3.5 shrink-0" />
                <span>{t("landing.guestKept")}</span>
              </p>
            </div>
            )}
            {known && (
              <div className="mb-3">
                <NewGuestToggle guest={known} open={newProfile} onToggle={() => { setNewProfile((o) => !o); setError(""); }} />
              </div>
            )}

            <AnimatePresence>
              {error && (
                <motion.p
                  key="err"
                  initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 text-red-400 text-sm mb-4"
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
                </motion.p>
              )}
            </AnimatePresence>

          </motion.div>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
}

function PageWrapper({ children }) {
  return (
    <div className="relative min-h-screen w-full overflow-hidden flex flex-col">
      <BrandBackground />

      {/* LTR in every language: the language switch keeps its place (owner's rule). */}
      <header dir="ltr" className="relative z-10 flex items-center gap-3 px-6 py-5">
        <img src={LOGO_URL} alt={t("common.brand")} className="h-8 w-8 rounded-lg ring-1 ring-amber-400/30" />
        <span className="font-display text-base text-amber-300 italic tracking-wide">{t("common.brand")}</span>
        <div className="ms-auto">
          <LanguageSwitch />
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-start px-6 pt-2 pb-6 sm:pt-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          {/* Invitation header */}
          <div className="text-center mb-7">
            <img
              src={EMBLEM_URL}
              alt=""
              draggable={false}
              width={760}
              height={657}
              className="mx-auto mb-2 w-auto h-28"
            />
            <h1 className="font-display text-3xl text-white mb-1">{t("join.invited")}</h1>
            <p className="text-slate-400 text-sm">{t("join.nameEnough")}</p>
          </div>

          {children}
        </motion.div>
      </main>
    </div>
  );
}
